/**
 * ★ OS TRÊS ESTADOS da D-11 corrigida — [spec-008 T036], US-3/US-4, N17.
 * Quinto irmão de `transporte-tempo-real.js`: aqui não há `RTCPeerConnection`,
 * nem `apiVoz`, nem DOM. Entra "o consultor mandou parar", sai o áudio
 * silenciado e o texto da fala corrente descartado — e **o job segue vivo**.
 *
 * ## O que a D-11 original quebrava, e o caso que a corrigiu
 *
 * O contrato mandava abortar a delegação em **todo** `interromper`
 * (`contracts/transporte.md:94`). Com o agente de voz isso quebra o caso
 * central: *"como está indo?"* é interrupção da FALA e cancelaria a consulta
 * que o consultor está perguntando sobre. São três estados distintos, e só o
 * último aborta:
 *
 * | estado | onde ele vive | `interromper` faz |
 * |---|---|---|
 * | texto gerado | os `output_transcript.delta` da fala corrente | **descarta** — param de subir, e `falaDaIaTerminou` fecha a fala |
 * | áudio reproduzido | a trilha REMOTA do `RTCPeerConnection` | **para a fala** — `track.enabled = false` |
 * | ação executada | o `function_call` em voo no `-delegacao.js` | **segue** — só `cancelarTrabalho()` o mata |
 *
 * Uma pergunta de status **não** invalida o resultado em voo (N17).
 *
 * ## ★ Por que a supressão termina por SILÊNCIO MEDIDO, e não no próximo delta
 *
 * Interromper não faz o provedor parar de gerar: os deltas da MESMA fala
 * continuam chegando (não existe evento cliente→servidor de cancelar fala — a
 * lista inteira está no `-protocolo.js`). Se a supressão caísse no primeiro
 * delta seguinte, ela duraria milissegundos e o consultor voltaria a ouvir a
 * fala que mandou parar.
 *
 * O discriminante entre "a mesma fala continuando" e "uma fala NOVA" é o
 * intervalo entre deltas, e ele foi MEDIDO nas 7 sessões: o maior intervalo
 * DENTRO de uma fala foi 2,13 s (263 amostras) e o menor ENTRE falas separadas
 * foi 5,02 s (26 amostras). `MS_SILENCIO_ATE_FIM_DA_FALA_DA_IA` (3 s) cai na
 * faixa medida como VAZIA — é a mesma constante que deriva `falaDaIaTerminou`,
 * e pelo mesmo motivo. Enquanto os deltas continuarem chegando, a supressão se
 * rearma; parou de chegar por 3 s, a próxima fala é outra e o áudio volta.
 *
 * ## O áudio para pela TRILHA, não por um `<audio>`
 *
 * `track.enabled = false` numa trilha remota faz ela produzir silêncio, sem
 * renegociar nada — e mantém este módulo **sem DOM**, como o núcleo de
 * medição. Quem anexa o `MediaStream` a um elemento continua sendo a tela
 * (`audioRemoto`), e ela não precisa saber que a fala foi cortada.
 */
import { MS_SILENCIO_ATE_FIM_DA_FALA_DA_IA } from './transporte-tempo-real-protocolo'

/**
 * O controle da FALA — os dois primeiros estados. O terceiro (o job) mora no
 * `-delegacao.js` e este módulo nunca o toca: é isso que faz o N17 valer.
 *
 * Uma instância por sessão, como o executor de delegação e pelo mesmo motivo:
 * a trilha remota é daquela `RTCPeerConnection`, e herdá-la faria a sessão
 * seguinte tentar silenciar uma trilha morta.
 */
export const criarControleDaFala = () => {
  let _trilhas = []
  let _suprimindo = false
  let _temporizadorDaSupressao = null

  const _limparTemporizador = () => {
    if (!_temporizadorDaSupressao) return
    clearTimeout(_temporizadorDaSupressao)
    _temporizadorDaSupressao = null
  }

  const _aplicarNasTrilhas = (habilitada) => {
    _trilhas.forEach((trilha) => {
      trilha.enabled = habilitada
    })
  }

  /**
   * A trilha remota que o provedor abriu. Guardada por SESSÃO — a tela recebe
   * o `MediaStream` inteiro por `audioRemoto` e o anexa onde quiser; o que
   * fica aqui é só o poder de silenciá-la.
   */
  const registrarTrilhaRemota = (stream) => {
    if (!stream || typeof stream.getAudioTracks !== 'function') return
    _trilhas = stream.getAudioTracks() || []
    // Trilha nova entra HABILITADA mesmo se a sessão anterior tinha sido
    // interrompida: supressão é estado de fala, não de conexão.
    _aplicarNasTrilhas(true)
  }

  const _liberarAFala = () => {
    _limparTemporizador()
    if (!_suprimindo) return
    _suprimindo = false
    _aplicarNasTrilhas(true)
  }

  const _rearmarSupressao = () => {
    _limparTemporizador()
    _temporizadorDaSupressao = setTimeout(
      _liberarAFala,
      MS_SILENCIO_ATE_FIM_DA_FALA_DA_IA
    )
  }

  /**
   * ★ O consultor mandou parar de falar. Silencia a trilha e passa a descartar
   * o texto da fala corrente. **Não toca no job** — é o ponto inteiro do N17.
   *
   * Chamado só quando a IA está de fato falando (o leitor de eventos é quem
   * sabe disso): suprimir com a IA calada engoliria a PRÓXIMA fala, que o
   * consultor não mandou parar.
   */
  const interromperFalaCorrente = () => {
    _suprimindo = true
    _aplicarNasTrilhas(false)
    _rearmarSupressao()
  }

  const suprimindo = () => _suprimindo

  /**
   * Um delta da fala interrompida chegou. O texto é DESCARTADO por quem
   * chama; aqui só se rearma a janela — enquanto o provedor continuar
   * despejando a mesma fala, o silêncio não se completa e o áudio não volta.
   */
  const notarDeltaSuprimido = () => {
    if (!_suprimindo) return
    _rearmarSupressao()
  }

  /** Teardown da sessão. Não reabilita trilha: ela morre com a conexão. */
  const limpar = () => {
    _limparTemporizador()
    _suprimindo = false
    _trilhas = []
  }

  return {
    registrarTrilhaRemota,
    interromperFalaCorrente,
    suprimindo,
    notarDeltaSuprimido,
    limpar,
  }
}
